import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

import Loader from "../components/Loader";
import AnalyticsHeader from "../components/event-analytics-preview/AnalyticsHeader";
import AttendeeValueCard from "../components/event-analytics-preview/AttendeeValueCard";
import ImpactSummaryRow from "../components/event-analytics-preview/ImpactSummaryRow";
import MetricSummaryRow from "../components/event-analytics-preview/MetricSummaryRow";
import { fetchData, postData } from "../utils/api";

const PreviewStatePanel = ({ title, description, tone = "default" }) => {
  return (
    <section
      className={`event-analytics-preview__state-panel event-analytics-preview__state-panel--${tone}`}
    >
      <h1>{title}</h1>
      <p>{description}</p>
    </section>
  );
};

const EventAnalyticsPreviewPage = () => {
  const { companySlug, eventId } = useParams();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["eventAnalyticsPreview", companySlug, eventId],
    queryFn: () => fetchData(`api/events/${eventId}/analytics`),
  });

  const overrideMutation = useMutation({
    mutationFn: (overrideData) => postData(`api/events/${eventId}/analytics/override`, overrideData),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["eventAnalyticsPreview", companySlug, eventId],
      });
      toast.success("Attendee value updated");
    },
  });

  if (isLoading) {
    return (
      <main className="event-analytics-preview">
        <section className="event-analytics-preview__loading-state">
          <Loader />
          <p>Loading event analytics preview...</p>
        </section>
      </main>
    );
  }

  if (error) {
    const status = error?.response?.status;

    if (status === 404) {
      return (
        <main className="event-analytics-preview">
          <PreviewStatePanel
            description="The requested event could not be found."
            title="Event not found"
            tone="warning"
          />
        </main>
      );
    }

    if (status === 403) {
      return (
        <main className="event-analytics-preview">
          <PreviewStatePanel
            description="You do not have access to this event analytics preview."
            title="Access denied"
            tone="warning"
          />
        </main>
      );
    }

    return (
      <main className="event-analytics-preview">
        <PreviewStatePanel
          description="Something went wrong while loading this preview. Please try again."
          title="Preview unavailable"
          tone="danger"
        />
      </main>
    );
  }

  const attendeeValue = data?.attendee_value;
  const canOverride = data?.can_override;
  const estimate = data?.estimate;
  const event = data?.event;
  const metrics = data?.metrics;

  if (!event || !metrics) {
    return (
      <main className="event-analytics-preview">
        <PreviewStatePanel
          description="The analytics payload is missing required preview data right now. Try refreshing in a moment."
          title="Preview unavailable"
          tone="warning"
        />
      </main>
    );
  }

  const backTo = companySlug ? `/companies/${companySlug}/events` : "/events";
  const hasTrackedTraffic = Number(metrics.ticket_page_visits) > 0;

  return (
    <main className="event-analytics-preview">
      <AnalyticsHeader backTo={backTo} event={event} />

      <section className="event-analytics-preview__section event-analytics-preview__section--impact">
        <MetricSummaryRow metrics={metrics} />
        {hasTrackedTraffic ? (
          <ImpactSummaryRow metrics={metrics} />
        ) : (
          <div className="event-analytics-preview__empty-card">
            <h3>Return metrics will appear once traffic arrives</h3>
            <p>
              ROI and ROAS need tracked ticket-page visits before they become meaningful, so this
              preview keeps the section quiet until there is signal to read.
            </p>
          </div>
        )}
      </section>

      <section className="event-analytics-preview__section event-analytics-preview__section--attendee">
        <div className="event-analytics-preview__attendee-layout">
          <div className="event-analytics-preview__attendee-copy">
            <div className="event-analytics-preview__section-intro event-analytics-preview__section-intro--attendee">
              <p className="event-analytics-preview__section-eyebrow">Attendee Value</p>
              <h2 className="event-analytics-preview__section-title">
                Choose the value that best represents the average attendee to one of your events.
              </h2>
              <p className="event-analytics-preview__section-copy">
                This helps us more accurately calculate the revenue generated by each attendee that
                Vesta drives to this event.
              </p>
            </div>
          </div>

          <AttendeeValueCard
            attendeeValue={attendeeValue}
            canOverride={canOverride}
            estimate={estimate}
            isSaving={overrideMutation.isPending || overrideMutation.isLoading}
            onSaveOverride={overrideMutation.mutateAsync}
          />
        </div>
      </section>
    </main>
  );
};

export default EventAnalyticsPreviewPage;
